import { Button } from "@frontend/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@frontend/components/ui/dialog"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import type { HangingProtocolSlotInput } from "@frontend/pages/case-workspace/radiology/hooks/use-hanging-protocols"
import { defaultLayouts } from "@niivue/nvreact"
import type { HangingProtocol } from "@shared/lib/hanging-protocol"
import { useCallback, useEffect, useMemo, useState } from "react"

interface EditableSlot {
  viewer_index: number
  text_contains: string
}

export interface HangingProtocolDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  layoutName: string
  onLayoutChange: (layout: string) => void
  activeViewerIndex: number
  viewerCount: number
  selectedProtocolId: string | null
  onSelectedProtocolIdChange: (id: string | null) => void
  protocols: HangingProtocol[]
  selectedProtocol: HangingProtocol | undefined
  isLoadingProtocols: boolean
  isMutating: boolean
  protocolError: string | null
  viewerError: string | null
  onApply: (protocol: HangingProtocol) => void
  onSetDefault: () => void
  onDelete: () => void
  onCreateProtocol: (payload: { name: string; layout_key: string; slots: HangingProtocolSlotInput[] }) => Promise<void>
  onEditProtocol: (payload: { name: string; layout_key: string; slots: HangingProtocolSlotInput[] }) => Promise<void>
  isCreating: boolean
  isUpdating: boolean
}

function slotCountForLayout(layoutKey: string): number {
  return defaultLayouts[layoutKey]?.slots ?? 1
}

function hydrateSlots(layoutKey: string, source: HangingProtocolSlotInput[] | undefined): EditableSlot[] {
  const count = slotCountForLayout(layoutKey)
  const byIndex = new Map<number, HangingProtocolSlotInput>()
  for (const slot of source ?? []) {
    byIndex.set(slot.viewer_index, slot)
  }
  return Array.from({ length: count }, (_, viewer_index) => {
    const current = byIndex.get(viewer_index)
    return { viewer_index, text_contains: current?.text_contains ?? "" }
  })
}

function normalizeProtocolSlots(protocol: HangingProtocol): HangingProtocolSlotInput[] {
  return protocol.slots.map((slot) => ({
    viewer_index: slot.viewer_index,
    text_contains: slot.text_contains
  }))
}

export function HangingProtocolDialog({
  open,
  onOpenChange,
  layoutName,
  onLayoutChange,
  activeViewerIndex,
  viewerCount,
  selectedProtocolId,
  onSelectedProtocolIdChange,
  protocols,
  selectedProtocol,
  isLoadingProtocols,
  isMutating,
  protocolError,
  viewerError,
  onApply,
  onSetDefault,
  onDelete,
  onCreateProtocol,
  onEditProtocol,
  isCreating,
  isUpdating
}: HangingProtocolDialogProps) {
  const [name, setName] = useState("")
  const [slots, setSlots] = useState<EditableSlot[]>(() => hydrateSlots(layoutName, undefined))
  const [formError, setFormError] = useState<string | null>(null)

  const layoutEntries = useMemo(
    () => Object.entries(defaultLayouts).map(([key, config]) => ({ key, label: config.label })),
    []
  )

  // Populate form when selected protocol changes
  useEffect(() => {
    if (selectedProtocol) {
      setName(selectedProtocol.name)
      setSlots(hydrateSlots(selectedProtocol.layout_key, normalizeProtocolSlots(selectedProtocol)))
    } else {
      setName("")
      setSlots(hydrateSlots(layoutName, undefined))
    }
    setFormError(null)
  }, [selectedProtocol, layoutName])

  // Update slot count when layout changes
  useEffect(() => {
    setSlots((current) => {
      const count = slotCountForLayout(layoutName)
      if (current.length === count) return current
      const byIndex = new Map(current.map((slot) => [slot.viewer_index, slot]))
      return Array.from({ length: count }, (_, viewer_index) => {
        const existing = byIndex.get(viewer_index)
        return existing ?? { viewer_index, text_contains: "" }
      })
    })
  }, [layoutName])

  const handleSlotChange = useCallback((viewerIndex: number, value: string) => {
    setSlots((current) =>
      current.map((slot) => (slot.viewer_index === viewerIndex ? { ...slot, text_contains: value } : slot))
    )
  }, [])

  const buildPayload = useCallback(() => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setFormError("Name is required.")
      return null
    }
    const payloadSlots = slots
      .map((slot) => ({ viewer_index: slot.viewer_index, text_contains: slot.text_contains.trim() || null }))
      .filter((slot) => Boolean(slot.text_contains))
    if (payloadSlots.length === 0) {
      setFormError("Add at least one slot rule before saving.")
      return null
    }
    setFormError(null)
    return { name: trimmedName, layout_key: layoutName, slots: payloadSlots }
  }, [name, slots, layoutName])

  const handleSave = useCallback(async () => {
    const payload = buildPayload()
    if (!payload) return
    if (selectedProtocol) {
      await onEditProtocol(payload)
    } else {
      await onCreateProtocol(payload)
    }
  }, [buildPayload, selectedProtocol, onEditProtocol, onCreateProtocol])

  const handleSaveAsNew = useCallback(async () => {
    const payload = buildPayload()
    if (!payload) return
    await onCreateProtocol(payload)
  }, [buildPayload, onCreateProtocol])

  const isSaving = isCreating || isUpdating

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(95vw,720px)] sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Hanging Protocols</DialogTitle>
          <DialogDescription>Configure viewer layout and manage hanging protocol presets.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>Layout</Label>
            <Select value={layoutName} onValueChange={(value) => value && onLayoutChange(value)}>
              <SelectTrigger className="w-36">
                <SelectValue>{layoutEntries.find((l) => l.key === layoutName)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {layoutEntries.map((layout) => (
                  <SelectItem key={layout.key} value={layout.key}>
                    {layout.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="pb-2 text-[11px] text-muted-foreground">
            Active viewer: {Math.min(activeViewerIndex + 1, Math.max(1, viewerCount))}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="hanging-protocol-select">Protocol</Label>
            <select
              id="hanging-protocol-select"
              value={selectedProtocolId ?? ""}
              onChange={(event) => onSelectedProtocolIdChange(event.target.value || null)}
              className="h-9 min-w-[200px] rounded-md border border-input bg-input/20 px-2 text-sm"
            >
              <option value="">None (new)</option>
              {protocols.map((protocol) => (
                <option key={protocol.id} value={protocol.id}>
                  {protocol.name}
                  {protocol.is_default ? " ★" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Prostate MR 2x2" />
          </div>
        </div>

        <div className="max-h-[40vh] w-full overflow-auto rounded-md border border-border">
          <div className="min-w-[300px]">
            <div className="grid grid-cols-[60px_1fr] gap-2 border-b border-border px-2 py-1.5 text-[10px] uppercase text-muted-foreground">
              <span>Viewer</span>
              <span>Series Text Contains</span>
            </div>
            {slots.map((slot) => (
              <div
                key={slot.viewer_index}
                className="grid grid-cols-[60px_1fr] gap-2 border-b border-border px-2 py-2 last:border-b-0"
              >
                <div className="flex items-center text-xs font-medium">{slot.viewer_index + 1}</div>
                <Input
                  value={slot.text_contains}
                  onChange={(event) => handleSlotChange(slot.viewer_index, event.target.value)}
                  placeholder="T2 / DWI / ADC..."
                />
              </div>
            ))}
          </div>
        </div>

        {formError ? <p className="text-xs text-destructive">{formError}</p> : null}
        {protocolError ? <p className="text-xs text-destructive">{protocolError}</p> : null}
        {viewerError ? <p className="text-xs text-destructive">{viewerError}</p> : null}
        {isLoadingProtocols ? <p className="text-xs text-muted-foreground">Loading protocols...</p> : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!selectedProtocol || isMutating}
            onClick={() => selectedProtocol && void onApply(selectedProtocol)}
          >
            Apply
          </Button>
          <Button size="sm" variant="outline" disabled={isSaving} onClick={() => void handleSave()}>
            {selectedProtocol ? "Save" : "Create"}
          </Button>
          {selectedProtocol ? (
            <Button size="sm" variant="outline" disabled={isSaving} onClick={() => void handleSaveAsNew()}>
              Save As New
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button size="sm" variant="outline" disabled={!selectedProtocol || isMutating} onClick={onSetDefault}>
            Set Default
          </Button>
          <Button size="sm" variant="destructive" disabled={!selectedProtocol || isMutating} onClick={onDelete}>
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
